import { z } from 'zod'
import { PageQuery } from './pagination.ts'
import { ISODate, Uuid, Version, blankAsAbsent, moneyField } from './primitives.ts'

/** ══ HỢP ĐỒNG API — BẢNG GIÁ và DANH MỤC VẬN HÀNH ═══════════════════════════════════════════ */

const priceVersionFields = {
  /** Bản RIÊNG của người tạo. Bỏ trống = bản CÔNG TY. */
  own: z.boolean().optional(),
  vendorId: blankAsAbsent(Uuid),
  fuelPrice: blankAsAbsent(moneyField('Giá dầu')),
  roundTo: blankAsAbsent(moneyField('Bước làm tròn')),
  roundMode: z.enum(['ceil', 'round', 'floor']).optional(),
  note: z.string().optional(),
}

export const CreatePriceVersionBody = z.object({
  name: z.string(),
  effectiveFrom: ISODate,
  ...priceVersionFields,
})

export const UpdatePriceVersionBody = z.object({
  version: Version,
  name: z.string().optional(),
  effectiveFrom: blankAsAbsent(ISODate),
  ...priceVersionFields,
})

export const DecidePriceVersionBody = z.object({
  version: Version,
  approve: z.boolean({ required_error: 'Thiếu `approve`.' }),
  rejectNote: z.string().optional(),
})

export const PriceVersionStepBody = z.object({ version: Version })
/** 🗑 Xoá các phiên giá công bố đã chọn (`price:manage`). Trần 200 cùng khuôn mọi lô ghi. */
export const DeletePriceVersionsBody = z.object({ ids: z.array(Uuid).min(1).max(200) })
export type DeletePriceVersionsBodyT = z.infer<typeof DeletePriceVersionsBody>
export const ListPriceVersionQuery = PageQuery.extend({ onDate: blankAsAbsent(ISODate) })
export type ListPriceVersionQueryT = z.infer<typeof ListPriceVersionQuery>
/** Danh sách không có bộ lọc riêng — vẫn phải phân trang (§8.3, không có ngoại lệ "bảng nhỏ"). */
export const ListOnlyPageQuery = PageQuery
export type ListOnlyPageQueryT = z.infer<typeof PageQuery>

export const OpsKind = z.enum(['cont_type', 'route', 'service_pack'])
export type OpsKind = z.infer<typeof OpsKind>

export const CreateOpsItemBody = z.object({
  name: z.string().optional(),
  note: z.string().optional(),
  /** Chỉ Dịch vụ mới có ký hiệu — luật đó ở máy chủ, lược đồ chỉ cho phép trường tồn tại. */
  code: z.string().optional(),
})

export const UpdateOpsItemBody = z.object({
  version: Version,
  name: z.string().optional(),
  note: z.string().optional(),
  active: z.boolean().optional(),
  code: z.string().optional(),
  /**
   * Thứ tự HIỆN RA của danh mục — mở cho sửa 18/09/2026 để màn có lối kéo–thả.
   * ⚠ Thứ tự này là thứ tự của chính Ô CHỌN DỊCH VỤ lúc tạo đơn (`ORDER BY sort_order, name`),
   *   nên nó là dữ liệu nghiệp vụ chứ không phải trang trí: mục hay dùng nhất phải đưa được lên đầu.
   */
  sortOrder: z.number().int().min(0).optional(),
})

export const OpsItemStepBody = z.object({ version: Version })
export const OpsKindParams = z.object({ kind: z.string() })
export const PricingIdParams = z.object({ id: Uuid })

/* ══ MỤC GIÁ CÔNG BỐ NHẬP TAY (🧮 Giá cước vendor) ═════════════════════════════════════════════ */

/**
 * ⚠ KHÔNG có `code` ở cửa TẠO: ký hiệu mục do MÁY CHỦ đặt (`nextCatCode`), đúng bản cũ. Cho lời
 *   gọi tự chọn là mở đường cho hai mục trùng ký hiệu.
 */
export const CreatePriceCatBody = z.object({})

export const UpdatePriceCatBody = z.object({
  version: Version,
  name: z.string().optional(),
  /** Tỷ lệ nhân — HỆ SỐ, không phải tiền, nên không đi qua `moneyField`. */
  ratio: z.coerce.number({ invalid_type_error: 'Tỷ lệ phải là số.' }).positive('Tỷ lệ phải lớn hơn 0.').optional(),
  roundTo: blankAsAbsent(moneyField('Bước làm tròn')),
  effectiveFrom: blankAsAbsent(ISODate),
  packId: blankAsAbsent(Uuid),
  active: z.boolean().optional(),
})

export const CreatePriceCatRowBody = z.object({})

export const UpdatePriceCatRowBody = z.object({
  version: Version,
  label: z.string().optional(),
  fromPlace: z.string().optional(),
  toPlace: z.string().optional(),
  contType: z.string().optional(),
  /** Chuỗi rỗng = BỎ nhà cung cấp khỏi dòng; `blankAsAbsent` sẽ nuốt mất ý đó nên không dùng. */
  vendorId: z.union([Uuid, z.literal('')]).optional(),
  buy: blankAsAbsent(moneyField('Giá mua')),
  /** `null` = theo tỷ lệ của MỤC. Khác hẳn `0` — xem `domain/price-cat.ts`. */
  ratio: z.union([z.coerce.number().positive('Tỷ lệ phải lớn hơn 0.'), z.null()]).optional(),
  note: z.string().optional(),
})

export const PriceCatIdParams = z.object({ catId: Uuid })
export type UpdatePriceCatBodyT = z.infer<typeof UpdatePriceCatBody>
export type UpdatePriceCatRowBodyT = z.infer<typeof UpdatePriceCatRowBody>

/* ══ GIÁ PHÂN BỔ (dầu · khấu hao vỏ lạnh) · GIÁ DẦU · LÃI VAY VỎ LẠNH ═════════════════════════ */

export const AllocKindParams = z.object({ kind: z.enum(['fuel', 'depreciation']) })

export const CreateAllocPriceBody = z.object({
  kind: z.enum(['fuel', 'depreciation']),
  effectiveFrom: ISODate,
  effectiveTo: blankAsAbsent(ISODate),
  price: moneyField('Đơn giá phân bổ'),
  unit: blankAsAbsent(z.string()),
  contType: blankAsAbsent(z.string()),
  note: blankAsAbsent(z.string()),
  /** Người khai đã XÁC NHẬN khép các kỳ cũ bị chồng về ngày liền trước kỳ mới (bản cũ user chốt 17/08). */
  closePrevious: z.boolean().optional(),
})

/** Soát trước khi lưu kỳ giá phân bổ: kỳ cũ bị chồng + đơn đã điền số. */
export const AllocClashQuery = z.object({
  kind: z.enum(['fuel', 'depreciation']),
  id: blankAsAbsent(Uuid),
  contType: blankAsAbsent(z.string()),
  effectiveFrom: ISODate,
  effectiveTo: blankAsAbsent(ISODate),
})

export const UpdateAllocPriceBody = z.object({
  version: Version,
  effectiveFrom: blankAsAbsent(ISODate),
  /** Chuỗi rỗng = BỎ ngày kết thúc (quay lại "còn hiệu lực") — `blankAsAbsent` sẽ nuốt mất ý đó. */
  effectiveTo: z.union([ISODate, z.literal('')]).optional(),
  price: blankAsAbsent(moneyField('Đơn giá phân bổ')),
  unit: z.string().optional(),
  contType: z.string().optional(),
  note: z.string().optional(),
  active: z.boolean().optional(),
  closePrevious: z.boolean().optional(),
})

export const SetFuelPriceBody = z.object({
  onDate: ISODate,
  price: moneyField('Giá dầu'),
  note: blankAsAbsent(z.string()),
})

export const SetReeferInterestBody = z.object({
  amount: moneyField('Lãi vay vỏ lạnh'),
  note: blankAsAbsent(z.string()),
})

export type CreateAllocPriceBodyT = z.infer<typeof CreateAllocPriceBody>
export type UpdateAllocPriceBodyT = z.infer<typeof UpdateAllocPriceBody>
export type AllocClashQueryT = z.infer<typeof AllocClashQuery>
export type SetFuelPriceBodyT = z.infer<typeof SetFuelPriceBody>
export type SetReeferInterestBodyT = z.infer<typeof SetReeferInterestBody>

/* ══ DÒNG GIÁ CỦA MỘT PHIÊN BẢN BẢNG GIÁ CÔNG BỐ (§C12c) ═════════════════════════════════════ */

export const ListPriceQuery = z.object({ versionId: Uuid })

/** Thống kê giá công bố theo giai đoạn — `extra` = một phiên CHỜ DUYỆT cần tóm tắt, tính thêm vào các kỳ đã duyệt. */
export const PriceStatsQuery = z.object({ extra: blankAsAbsent(Uuid) })
export type PriceStatsQueryT = z.infer<typeof PriceStatsQuery>

/**
 * ＋ LẤY DÒNG GIÁ VỐN — nạp một loạt dòng giá vốn vào phiên bản với cùng một markup.
 * ⚠ `markupPct` là PHẦN TRĂM, không phải tiền — nên `z.number()`, không `moneyField`.
 */
/** Tờ bảng giá công bố đường sắt tại một ngày — bỏ trống = hôm nay. */
export const RailBoardQuery = z.object({ onDate: blankAsAbsent(ISODate) })

export const AddListPriceRowsBody = z.object({
  versionId: Uuid,
  costRateIds: z.array(Uuid).min(1, 'Chưa chọn dòng giá vốn nào.').max(1000, 'Tối đa 1.000 dòng một lượt.'),
  markupPct: z.number().min(-100, 'Markup không thể nhỏ hơn −100 %.').max(1000, 'Markup tối đa 1.000 %.'),
})

/** ✅ Ban hành giá công bố từ bảng giá vốn đường sắt — tỷ lệ riêng từng nhóm, trình đích danh người duyệt. */
export const PublishFromCostBody = z.object({
  name: z.string().trim().min(1, 'Thiếu tên bảng giá.'),
  effectiveFrom: ISODate,
  vendorId: blankAsAbsent(Uuid),
  approverId: Uuid,
  fuelPrice: blankAsAbsent(moneyField('Giá dầu')),
  note: z.string().max(2000),
  groups: z.array(z.object({
    costRateIds: z.array(Uuid).max(5000),
    markupPct: z.number().min(-100, 'Markup không thể nhỏ hơn −100 %.').max(1000, 'Markup tối đa 1.000 %.'),
  })).min(1).max(4),
})
export type PublishFromCostBodyT = z.infer<typeof PublishFromCostBody>

/** Chuột phải ▸ chép % của một dòng xuống các dòng phía dưới trong nhóm đang mở — bỏ luôn giá gõ tay của các dòng đó. */
export const CopyMarkupBody = z.object({
  versionId: Uuid,
  ids: z.array(Uuid).min(1, 'Không có dòng nào phía dưới.').max(1000, 'Tối đa 1.000 dòng một lượt.'),
  markupPct: z.number().min(-100, 'Markup không thể nhỏ hơn −100 %.').max(1000, 'Markup tối đa 1.000 %.'),
})
export type CopyMarkupBodyT = z.infer<typeof CopyMarkupBody>

/**
 * Sửa một dòng.
 * ⚠ `priceOverride` NHẬN `null` để XOÁ số gõ tay — khác `undefined` (không đụng tới). Dùng
 *   `nullable().optional()` chứ không `blankAsAbsent`: ở đây chuỗi rỗng và `null` phải khác nhau
 *   được với `undefined`.
 */
export const SetListPriceRowBody = z.object({
  version: Version,
  /* ⚠ `blankAsAbsent` chứ không `.optional()` trần: ô form để trống gửi `''`, và `.optional()`
     trần từ chối nó — `tools/blank-field` canh đúng chuyện đó. */
  markupPct: blankAsAbsent(z.coerce.number()),
  priceOverride: z.string().nullable().optional(),
  note: z.string().nullable().optional(),
})

export const ListPriceIdParams = z.object({ id: Uuid })
/** Thân RỖNG nhưng vẫn khai (`route-phai-khai-luoc-do`) — khoá lạ bị gạt thay vì đi tiếp. */
export const ListPriceNoBody = z.object({}).strict()

export type ListPriceQueryT = z.infer<typeof ListPriceQuery>
export type AddListPriceRowsBodyT = z.infer<typeof AddListPriceRowsBody>
export type SetListPriceRowBodyT = z.infer<typeof SetListPriceRowBody>

export type RailBoardQueryT = z.infer<typeof RailBoardQuery>

/* ══ GIÁ CHO FORM ĐƠN HÀNG (chép `azActivePriceRows` · `azRailComboPrice`) ══════════════════════════ */

/** Ngày lấy hàng + (tuỳ chọn) hai ga và phường/tỉnh hai đầu để gộp giá trọn gói đường sắt. */
export const OrderPriceQuery = z.object({
  onDate: ISODate,
  fromStation: blankAsAbsent(z.string().max(200)),
  toStation: blankAsAbsent(z.string().max(200)),
  fromWard: blankAsAbsent(z.string().max(200)),
  fromProvince: blankAsAbsent(z.string().max(200)),
  toWard: blankAsAbsent(z.string().max(200)),
  toProvince: blankAsAbsent(z.string().max(200)),
  cont: blankAsAbsent(z.string().max(50)),
})

export const OrderAllocQuery = z.object({
  onDate: ISODate,
  contType: blankAsAbsent(z.string().max(50)),
  routeName: blankAsAbsent(z.string().max(200)),
})

export type OrderPriceQueryT = z.infer<typeof OrderPriceQuery>
export type OrderAllocQueryT = z.infer<typeof OrderAllocQuery>
